import { memo } from 'react';
import { CampaignHeader } from '../CampaignHeader';
import { StrategicElementsGrid } from '../StrategicElementsGrid';
import { ConstraintsGrid } from '../ConstraintsGrid';
import { IdeasShowcase } from '../IdeasShowcase';
import { BenchmarkCarousel } from '../BenchmarkCarousel';
import type { IdeaListPanelProps } from './IdeaListPanel.types';
import { useTranslation } from 'react-i18next';

export const IdeaListPanel = memo(function IdeaListPanel({
  ideas,
  summary,
  benchmarkReferences,
  campaignTitle,
  mainImage,
  additionalImages,
  onIdeaSelect,
  exporting = false,
}: IdeaListPanelProps) {
  const { t } = useTranslation('contentTest');

  // Live short-circuits to a bare text node; export still renders the rest of
  // the overview (header, elements, constraints, benchmarks). The read schema
  // (getSingleCampaign) accepts an empty `ideas`, so this IS reachable on export.
  if (ideas.length === 0 && !exporting) {
    return t('ideationWidget.ideaListPanel.noIdeasAvailable');
  }

  return (
    <div className="flex flex-col">
      {summary && (
        <CampaignHeader
          title={campaignTitle || t('ideationWidget.ideaListPanel.campaignOverview')}
          creativeMission={summary.creativeMission}
          mainImage={mainImage}
          additionalImages={additionalImages}
        />
      )}

      {summary?.keyStrategicElements && summary.keyStrategicElements.length > 0 && (
        <StrategicElementsGrid elements={summary.keyStrategicElements} />
      )}

      {summary?.constraints && summary.constraints.length > 0 && (
        <ConstraintsGrid constraints={summary.constraints} />
      )}

      {benchmarkReferences && benchmarkReferences.length > 0 && (
        <BenchmarkCarousel references={benchmarkReferences} exporting={exporting} />
      )}

      <IdeasShowcase ideas={ideas} onIdeaSelect={onIdeaSelect} exporting={exporting} />
    </div>
  );
});
